/*
 * THE TIMELINE TAB AS THE FRAMES DRAW IT, on top of buildTimeline.
 *
 * record/timelineModel.mjs turns GET /companies/:id/timeline into rows a person
 * reads (who, the first line, the folds, the day groups). This module is the
 * tab's own last mile, the things the frames (1a, 1b, 1c, 1d, 1g) draw that are
 * about LAYOUT rather than about the data:
 *
 *   - SAME-MINUTE ORDER. A stage move and the message that caused it share a
 *     minute, and the server breaks the tie on row id, which is noise. The frames
 *     put the move above its message ("Pluto → Agreement + legal · you approved"
 *     over "You → Shahar: the agreement is attached"), except the move to New,
 *     which sits under the arrival it came from: New is where the record starts.
 *   - ONE ROW PER CALL. Fireflies' recording and Google's meeting are the same
 *     call; the frame draws one row ("Call · Northwind & Remotion · 32 min ·
 *     recorded · 1 calendar email folded"), so the meeting folds under its
 *     recording, with its calendar mail and its Held / No-show.
 *   - NOTES ON A MEETING. "Add notes" saves a call note carrying the meeting's
 *     event id; it reads under that meeting, not as a second row.
 *   - The row words the frames add: "You:" on a one-person company, the quote of
 *     a pricing email and the Cc in the sub-line, "PandaDoc: Tom Becker viewed …
 *     · 3 views so far", "Dana: Get a proposal form · …" with "5 answers · 07:05
 *     ET", "from the intro email" on the move to New, the park's reason and its
 *     "Remind in 6 months · Oct 1", and "No signed copy on record" under a win
 *     that has none (reality.txt [1]).
 *   - What starts open: the newest move a PERSON decided about where the company
 *     stands now (Pluto's Closed won, Northwind's Agreement + legal). An
 *     automatic arrival (Dana) and a park (Fieldline) start closed, as drawn.
 *   - When the chips and the scope line show at all: on a list worth filtering
 *     (two people or more, or more than a dozen entries). Dana's two entries and
 *     Fieldline's handful are drawn without them.
 *
 * Pure: test/record-timeline-view.test.js pins it on Pluto- and Northwind-shaped
 * fixtures.
 *
 *   timelineView(rows, { company, contact, act, zones, filter, only, total,
 *                        hasMore, now, timeZone })
 *     → buildTimeline's result, plus { showFilters, scope, defaultOpen,
 *       empty }, `showing` and `start` reworded, and every row carrying what
 *       TimelineRow draws.
 */
import { buildTimeline, isCalendarMail, actorWords } from "./timelineModel.mjs";
import { pricingQuote, gmailThreadUrl, callSummaryPoints, cleanBody } from "../timelineModel.mjs";
import { formAnswers, formMessage } from "../formAnswers.mjs";
import { theirClock, sameClock } from "./zones.mjs";
import { REASON_LABELS } from "./endings.mjs";
import { hasSignedCopy } from "./situation.mjs";
import { toDate, clock, shortDate, plural, firstName, fullName, daysBetween } from "./format.mjs";
import { isMeetingNote } from "./meetingNotes.mjs";

const MINUTE = 60000;
const minuteOf = (ts) => {
  const d = toDate(ts);
  return d ? Math.floor(d.getTime() / MINUTE) : null;
};

const isCompanyMove = (r) =>
  r && r.item_type === "stage_transition" && !r.contact_id && (r.subtype === "status" || r.subtype === "pipeline_stage");
const isMoveToNew = (r) => isCompanyMove(r) && r.subtype === "pipeline_stage" && r.to_value === "new";

/* ------------------------------------------------------------ same-minute order */

/**
 * Rows newest first, with each run of same-minute rows reordered: company moves
 * first (they are the consequence, so they read as newer), then everything else,
 * then the move to New (the record's start, under the arrival that caused it).
 * Stable inside each rank, so two emails in one minute keep the server's order.
 */
export function orderTies(rows) {
  const list = (rows || []).filter(Boolean);
  const rank = (r) => (isMoveToNew(r) ? 2 : isCompanyMove(r) ? 0 : 1);
  const out = [];
  let i = 0;
  while (i < list.length) {
    const m = minuteOf(list[i].occurred_at);
    let j = i + 1;
    while (j < list.length && m !== null && minuteOf(list[j].occurred_at) === m) j++;
    const run = list.slice(i, j).map((r, k) => ({ r, k }));
    run.sort((a, b) => rank(a.r) - rank(b.r) || a.k - b.k);
    for (const { r } of run) out.push(r);
    i = j;
  }
  return out;
}

/* ------------------------------------------------------------ meetings and calls */

/**
 * The key the server keys a meeting's Held / No-show on (call_outcomes): the
 * Google event id, or the row's own id for a meeting with no external id. Null
 * when neither applies, and then the row offers no Held / No-show at all.
 */
export function meetingEventId(row) {
  if (!row || row.subtype !== "meeting") return null;
  if (row.event_id) return String(row.event_id);
  return row.external_id ? null : String(row.id);
}

/**
 * THE MEETING HELD / NO-SHOW ANSWERS, for Next's "Update stage?" line. That line
 * is about facts.last_meeting_at (the server's newest past meeting, "[Cancelled]"
 * titles left out), so the row is the one starting within a minute of it; only
 * when none does (a payload without the fact, a row on an older page) the newest
 * past meeting that was not cancelled. Never simply the newest past meeting row: a
 * cancelled or unrelated later invite in the company timeline would take the
 * outcome, and the call that happened would stay unrecorded.
 */
export function meetingForOutcome(rows, { at = null, now = new Date() } = {}) {
  const until = toDate(now).getTime();
  const cancelled = (r) => /^\[cancelled\]/i.test(String(r.title || "")) || (r.metadata && r.metadata.status === "cancelled");
  const past = (rows || []).filter((r) => r && r.subtype === "meeting" && toDate(r.occurred_at) && toDate(r.occurred_at).getTime() <= until && !cancelled(r));
  const target = toDate(at);
  if (target) {
    const hit = past.find((r) => Math.abs(toDate(r.occurred_at).getTime() - target.getTime()) <= 60000);
    if (hit) return hit;
  }
  return past.slice().sort((a, b) => toDate(b.occurred_at) - toDate(a.occurred_at))[0] || null;
}

const joinLink = (m) => {
  const meta = (m && m.metadata) || {};
  return meta.hangout_link || meta.conference || null;
};

/**
 * Which meeting a recorded call is: the same Meet link, else the same title
 * within the day, else the nearest meeting starting within the hour. Null when
 * none: a call with no calendar entry is its own row.
 */
export function meetingForCall(call, meetings) {
  const at = toDate(call && call.occurred_at);
  if (!at || !meetings || !meetings.length) return null;
  const link = call.metadata && call.metadata.meeting_link;
  if (link) {
    const byLink = meetings.find((m) => joinLink(m) && joinLink(m) === link);
    if (byLink) return byLink;
  }
  const gap = (m) => Math.abs(toDate(m.occurred_at) - at);
  const title = String(call.title || "").trim().toLowerCase();
  const sameTitle = meetings.filter((m) => title && String(m.title || "").trim().toLowerCase() === title && gap(m) <= 12 * 3600000);
  const pool = sameTitle.length ? sameTitle : meetings.filter((m) => gap(m) <= 3600000);
  if (!pool.length) return null;
  return pool.reduce((best, m) => (gap(m) < gap(best) ? m : best));
}

/*
 * Where buildTimeline will fold a calendar email: the meeting its subject names,
 * else the nearest meeting. The same rule as timelineModel.mjs, so a meeting that
 * folds into its call takes its own calendar mail along instead of leaving it to
 * fall on some other meeting.
 */
function homeOf(mail, meetings) {
  const subject = String(mail.title || "").toLowerCase();
  const byTitle = meetings.find((m) => m.title && subject.includes(String(m.title).toLowerCase()));
  if (byTitle) return byTitle;
  if (!meetings.length) return null;
  const t = toDate(mail.occurred_at);
  return meetings.reduce((best, m) => (Math.abs(toDate(m.occurred_at) - t) < Math.abs(toDate(best.occurred_at) - t) ? m : best));
}

/**
 * The folds this tab adds before buildTimeline runs:
 *   { rows, callMeeting: Map(callId → { meeting, calendar }), notes: Map(meetingId → [note]) }
 * The rows that fold are left out of `rows`, so every count buildTimeline makes
 * ("29 entries", "Showing 200 of 340") counts what is drawn.
 */
export function foldCalls(rows) {
  const list = (rows || []).filter(Boolean);
  const meetings = list.filter((r) => r.subtype === "meeting");
  const notes = new Map();
  const noteIds = new Set();
  for (const n of list.filter(isMeetingNote)) {
    const key = String(n.metadata.event_id);
    const m = meetings.find((x) => meetingEventId(x) === key || String(x.id) === key);
    if (!m) continue;
    if (!notes.has(m.id)) notes.set(m.id, []);
    notes.get(m.id).push(n);
    noteIds.add(n.id);
  }
  const callMeeting = new Map();
  const folded = new Set();
  for (const c of list.filter((r) => r.subtype === "call" && !noteIds.has(r.id))) {
    const m = meetingForCall(c, meetings.filter((x) => !folded.has(x.id)));
    if (!m) continue;
    folded.add(m.id);
    callMeeting.set(c.id, { meeting: m, calendar: 0 });
  }
  const dropped = new Set([...noteIds, ...folded]);
  if (folded.size) {
    for (const r of list) {
      if (!isCalendarMail(r)) continue;
      const home = homeOf(r, meetings);
      if (!home || !folded.has(home.id)) continue;
      for (const v of callMeeting.values()) if (v.meeting.id === home.id) v.calendar += 1;
      dropped.add(r.id);
    }
  }
  return { rows: list.filter((r) => !dropped.has(r.id)), callMeeting, notes };
}

/* ------------------------------------------------------------ words */

/** "lukasz@remotion.io" → "Lukasz": the first word of each address, capitalised. */
export function ccNames(emails) {
  return (emails || [])
    .filter((e) => typeof e === "string" && e.includes("@"))
    .map((e) => {
      const word = e.split("@")[0].split(/[._+-]/)[0];
      return word ? word.charAt(0).toUpperCase() + word.slice(1) : e;
    });
}

const MONEY = /[$€£₪]\s?\d[\d,.]*\s?[kKmM]?(?:\s?\/\s?mo(?:nth)?)?/;

/** "budget $40K/mo", from a Fireflies summary's Budget point; null without one. */
export function callBudget(summary) {
  const point = callSummaryPoints(summary || "").find((p) => p.label && /budget|spend/i.test(p.label));
  const m = point && MONEY.exec(point.text);
  return m ? `budget ${m[0].replace(/\s+/g, "")}` : null;
}

/** "Remind in 6 months · Oct 1", from the move's date to the wake-up. */
export function remindLine(fromTs, untilTs, { now = new Date() } = {}) {
  const days = daysBetween(fromTs, untilTs);
  if (days === null || days < 0) return null;
  const when = shortDate(untilTs, { now });
  if (days >= 56) return `Remind in ${plural(Math.round(days / 30.44), "month")} · ${when}`;
  if (days >= 14) return `Remind in ${plural(Math.round(days / 7), "week")} · ${when}`;
  return `Remind in ${plural(days, "day")} · ${when}`;
}

// The arrival a move to New came from, in the frames' words.
function arrivalWords(row) {
  if (!row) return null;
  if (row.subtype === "form_submission") return "from the form";
  if (row.subtype === "email") {
    const meta = row.metadata || {};
    if (meta.introduced_by) return "from the intro email";
    const dir = row.direction || meta.direction;
    if (dir === "inbound") return "from their email";
    if (dir === "outbound") return "from your email";
  }
  if (row.subtype === "linkedin_message") return "from the LinkedIn DM";
  if (row.subtype === "whatsapp_message") return "from the WhatsApp DM";
  return null;
}

// A move's line with one more phrase before who made it: "Fieldline → Parked ·
// stopped chasing · set by you". The actor is the model's own words, so the cut
// is exact.
function withPhrase(text, item, phrase) {
  if (!phrase) return text;
  const actor = actorWords(item);
  const tail = actor ? ` · ${actor}` : "";
  const base = tail && text.endsWith(tail) ? text.slice(0, -tail.length) : text;
  return `${base} · ${phrase}${base === text ? "" : tail}`;
}

/* ------------------------------------------------------------ the tab */

const OUT_STATUSES = new Set(["recycled", "lost", "not_a_fit", "churned"]);

/**
 * The whole tab. See the header for what it adds; everything buildTimeline
 * returns is passed through unchanged.
 */
export function timelineView(rows, { company = null, contact = null, act = null, zones = null, filter = "all", only = null, total = null, hasMore = false, now = new Date(), timeZone } = {}) {
  const ordered = orderTies(rows);
  const { rows: kept, callMeeting, notes } = foldCalls(ordered);
  const tl = buildTimeline(kept, { company, contact, filter, only, total, hasMore, now, timeZone });

  const people = ((company && company.contacts) || []).filter((c) => c && c.kind !== "junk");
  const byEmail = new Map(people.filter((p) => p.email).map((p) => [String(p.email).toLowerCase(), p]));
  const nameFor = (email) => {
    const p = byEmail.get(String(email || "").toLowerCase());
    return p ? fullName(p) : email;
  };
  const onePerson = people.length <= 1;
  const theirs = zones && zones.theirs ? zones.theirs : null;
  const mineTz = zones && zones.mine ? zones.mine.tz : timeZone;
  const theirTime = (ts) => (theirs && !(mineTz && sameClock(theirs.tz, mineTz, ts)) ? theirClock(ts, theirs) : null);

  // Views of every visible row, newest first, for the neighbour look-ups below.
  const all = tl.days.flatMap((d) => d.rows);

  // PandaDoc views per document, over the whole loaded list (filters aside): the
  // newest view of a document says how many there have been so far.
  const views = kept.filter((r) => r.item_type === "document" && r.event === "viewed");
  const viewCount = new Map();
  for (const r of views) {
    const k = r.document_name || "";
    viewCount.set(k, (viewCount.get(k) || 0) + 1);
  }
  const newestView = new Map();
  for (const r of views) if (!newestView.has(r.document_name || "")) newestView.set(r.document_name || "", r.id);

  // The newest company status move, for the park's reason and wake-up.
  const newestStatus = kept.find((r) => isCompanyMove(r) && r.subtype === "status") || null;
  const newestWin = kept.find((r) => isCompanyMove(r) && r.subtype === "status" && r.to_value === "client") || null;

  for (const v of all) {
    const it = v.item;
    const meta = it.metadata || {};
    switch (v.kind) {
      case "email": {
        if (onePerson && v.who.startsWith("You → ")) v.who = "You";
        const snippet = v.sub.includes("snippet only");
        const rest = v.sub.filter((s) => s !== "snippet only");
        const quote = v.direction === "outbound" && !meta.bulk ? pricingQuote(it.body) : null;
        if (quote && quote.terms.length) {
          const terms = quote.terms.join(" · ");
          if (!v.text.includes(quote.terms[0])) rest.push(terms);
          v.quote = quote;
        }
        const cc = ccNames(meta.cc_emails);
        if (cc.length) rest.push(`Cc ${cc.join(", ")}`);
        if (snippet) rest.push("snippet only");
        v.sub = rest;
        v.ccNames = cc;
        // Recipients and sender, said in full in the expansion.
        const holders = (it.holders || []).map((h) => fullName(h)).filter(Boolean);
        v.direction_line =
          v.direction === "outbound"
            ? `Sent by you${holders.length ? ` to ${holders.join(", ")}` : ""}`
            : v.direction === "inbound"
              ? v.who === "inbound"
                ? `Received${holders.length ? ` by ${holders.join(", ")}` : ""} · sender not recorded`
                : `From ${it.sender ? fullName(it.sender) : v.who}${meta.introduced_by && holders.length ? `, copied to ${holders.join(", ")}` : ""}`
              : "Direction not recorded";
        v.gmail = (v.thread && v.thread.url) || gmailThreadUrl(it.msg_id) || null;
        v.attachment = it.attachment || null;
        break;
      }
      case "dm":
        v.attachment = it.attachment || null;
        v.direction_line = (it.direction || meta.direction) === "outbound" ? "Sent by you" : "Received";
        break;
      case "document": {
        v.sep = ": ";
        v.marker = "dot";
        const k = it.document_name || "";
        const n = viewCount.get(k) || 0;
        if (it.event === "viewed" && n > 1 && newestView.get(k) === it.id) v.text = `${v.text} · ${n} views so far`;
        const ref = String(it.ref || meta.ref || "");
        v.gmail = ref.startsWith("gmail:") ? gmailThreadUrl(ref.slice("gmail:".length)) : null;
        v.document.viewer_email = it.viewer_email || meta.viewer_email || null;
        break;
      }
      case "form": {
        // "Get a proposal form": the form's own name, said as a form, the way the
        // source pill says it (situation.mjs sourcePill).
        const name = String((meta.form && (meta.form.form_name || meta.form.name)) || meta.form_name || it.title || "Website form").trim();
        const formName = /\bform$/i.test(name) ? name : `${name} form`;
        const answers = formAnswers(it);
        const message = formMessage(it);
        const line = message ? cleanBody(message).replace(/\s+/g, " ") : "";
        const short = line.length > 80 ? `${line.slice(0, 80).trimEnd()}…` : line;
        v.text = short ? `${formName} · ${short}` : answers.length ? `${formName} · ${plural(answers.length, "answer")}` : formName;
        v.sub = [short && answers.length ? plural(answers.length, "answer") : null, theirTime(it.occurred_at)].filter(Boolean);
        v.answers = answers;
        v.message = message || null;
        break;
      }
      case "call": {
        const merged = callMeeting.get(it.id) || null;
        const mins = v.call.minutes;
        const recorded = it.source === "fireflies";
        v.who = it.title ? `Call · ${it.title}` : "Call";
        v.sep = " · ";
        v.text = [mins ? `${mins} min` : null, recorded ? "recorded" : "notes"].filter(Boolean).join(" · ");
        const budget = recorded ? callBudget(it.body) : null;
        v.sub = [v.call.todos.line || null, budget, merged && merged.calendar ? `${plural(merged.calendar, "calendar email")} folded` : null].filter(Boolean);
        v.call.attendees = (Array.isArray(meta.participants) ? meta.participants : []).map((p) => (typeof p === "string" ? nameFor(p) : fullName(p) || p.email)).filter(Boolean);
        v.call.points = callSummaryPoints(it.body || "");
        v.call.meeting = merged ? meetingFacts(merged.meeting, { now, timeZone, nameFor }) : null;
        break;
      }
      case "meeting": {
        const facts = meetingFacts(it, { now, timeZone, nameFor });
        Object.assign(v.meeting, facts);
        // The status and the controls are drawn by the row; the sub keeps only the
        // folded calendar mail, which reads last.
        v.folded = v.sub.find((s) => /calendar emails? folded$/.test(s)) || null;
        v.sub = [];
        v.notes = (notes.get(it.id) || []).map((n) => ({ id: n.id, at: n.occurred_at, body: n.body || "" }));
        v.noteTarget = {
          contact_id: it.contact_id || (contact && contact.id) || null,
          company_id: (company && company.id) || null,
          occurred_at: it.occurred_at,
          title: it.title || "Meeting",
          event_id: facts.event_key || String(it.id),
        };
        if (!facts.past && theirs) {
          const t = theirTime(it.occurred_at);
          if (t) v.meeting.their_time = `${t} for ${firstName(contact) || "them"}`;
        }
        break;
      }
      case "stage": {
        if (!v.milestone) break;
        const item = v.items ? v.items.find((x) => x.subtype === "status") || v.item : v.item;
        // "Pluto → New · from the intro email · set by you": the arrival beside it.
        if (isMoveToNew(v.item) && !/ · from /.test(v.text)) {
          const at = minuteOf(v.at);
          const arrival = all.find((x) => x !== v && x.kind !== "stage" && minuteOf(x.at) !== null && Math.abs(minuteOf(x.at) - at) <= 1);
          v.text = withPhrase(v.text, v.item, arrivalWords(arrival && arrival.item));
        }
        // The park's reason and wake-up, on the move that put it where it is now.
        if (newestStatus && item.id === newestStatus.id && company && company.status === item.to_value && OUT_STATUSES.has(item.to_value)) {
          const reason = company.status_reason && REASON_LABELS[company.status_reason];
          if (reason) v.text = withPhrase(v.text, item, reason.toLowerCase());
          if (item.to_value === "recycled" && company.recycle_until) {
            const line = remindLine(v.at, company.recycle_until, { now });
            if (line) v.sub = [...v.sub, line];
          }
          if (item.to_value === "lost" && company.re_engage_after) v.sub = [...v.sub, `Parks itself on ${shortDate(company.re_engage_after, { now })}`];
        }
        // A win with no signed copy says so, under the move (reality.txt [1]).
        if (newestWin && (item.id === newestWin.id || (v.items || []).some((x) => x.id === newestWin.id)) && !hasSignedCopy(company, act)) {
          v.evidenceLines = [...(v.evidenceLines || []), "No signed copy on record"];
        }
        break;
      }
      default:
        break;
    }
  }

  const entries = tl.entries;
  const showFilters = tl.everyone || entries > 12;
  const onlyName = only ? ((tl.onlyOptions.find((o) => String(o.id) === String(only)) || {}).name || null) : null;
  const scope = tl.everyone && company
    ? {
        label: `Everyone at ${company.name}`,
        rest: [plural(tl.people, "person", "people"), plural(entries, "entry", "entries")].join(" · "),
        short: plural(tl.people, "person", "people"),
        only: onlyName ? { label: `Only ${onlyName}`, count: plural(all.length, "entry", "entries") } : null,
        offer: contact && !onlyName ? { id: contact.id, label: `Only ${firstName(contact)}` } : null,
      }
    : null;

  return {
    ...tl,
    // Loaded against the server's total, both raw: the total counts every row the
    // server holds, folds included, so the rows on hand must be counted the same way.
    showing: hasMore && total ? `Showing ${(rows || []).length} of ${total}` : null,
    showFilters,
    scope,
    defaultOpen: defaultOpenKeys(all, company),
    // The start line only once there is something to start: two entries need no
    // footer to say they are all there is.
    start: tl.start && entries > 2 ? tl.start : null,
    empty: !all.length ? (entries ? "Nothing here under this filter." : "Nothing on the record yet.") : null,
  };
}

/** What a meeting row says beyond its title: when, who, whether it happened. */
export function meetingFacts(row, { now = new Date(), timeZone, nameFor = (e) => e } = {}) {
  const meta = (row && row.metadata) || {};
  const past = toDate(row.occurred_at) <= toDate(now);
  const attendees = (Array.isArray(meta.attendees) ? meta.attendees : [])
    .map((a) => {
      if (!a) return null;
      const email = typeof a === "string" ? a : a.email;
      const name = (typeof a === "object" && (a.name || a.displayName)) || nameFor(email) || email;
      const response = typeof a === "object" && a.response && a.response !== "needsAction" ? a.response : null;
      return name ? { name, response } : null;
    })
    .filter(Boolean);
  const cancelled = meta.status === "cancelled" || /^\[cancelled\]/i.test(String(row.title || ""));
  const outcome = row.outcome ? row.outcome.outcome : null;
  return {
    past,
    cancelled,
    outcome,
    event_key: meetingEventId(row),
    range: meta.end_at ? `${clock(row.occurred_at, { timeZone })}–${clock(meta.end_at, { timeZone })}` : clock(row.occurred_at, { timeZone }),
    attendees,
    organizer: meta.organizer || null,
    join: joinLink(row) || null,
    status: cancelled ? "Cancelled" : outcome === "held" ? "Held" : outcome === "no_show" ? "No-show" : past ? "Not recorded" : "Upcoming",
  };
}

/**
 * The rows that start open: the newest company move, when a person decided it
 * (approved or set by hand), it is where the company stands now, and the company
 * is in the pipeline or a client. The frames open exactly those (1a, 1d) and
 * leave Dana's automatic New and Fieldline's park closed (1c, 1g).
 */
export function defaultOpenKeys(rows, company) {
  if (!company) return [];
  const move = (rows || []).find((v) => v.kind === "stage" && v.milestone);
  if (!move) return [];
  if (company.status !== "prospect" && company.status !== "client") return [];
  const items = move.items || [move.item];
  const decided = items.some((x) => x.actor === "you_approved" || x.actor === "by_hand");
  const current = items.some((x) => (x.subtype === "status" ? x.to_value === company.status : x.to_value === company.pipeline_stage || (company.status === "client" && x.to_value === "closed_won")));
  return decided && current && (move.evidenceLines || []).length ? [move.key] : [];
}
